/**
 * Asking the browser not to throw the pairing away.
 *
 * A headset's pairing is a device token in `localStorage`, and browsers treat
 * storage as *best-effort* by default: when the device runs low on space, an
 * origin's data can be evicted with nobody touching anything. A headset that
 * loses its token shows a pairing code again -- while the console still lists
 * it, still holding a licence seat -- and somebody has to work out why.
 *
 * `navigator.storage.persist()` moves the origin to *persistent*, which is
 * exempt from that eviction. Chromium grants it on its own heuristics, and an
 * installed PWA used regularly is the case it grants. There is no prompt.
 *
 * This is the only cause of a headset unpairing itself that can be prevented
 * in code. The others are somebody clearing site data, a different Quest user
 * profile, a factory reset, or the app moving to a different origin -- and
 * that last one will unpair every headset exactly once, the day a real domain
 * replaces `localhost` (M5).
 */

/**
 * The part of `navigator.storage` this needs, so a test can hand one over.
 *
 * Properties rather than methods: they are always called on the manager
 * itself, and declaring them as methods invites `this` to be lost if one is
 * ever pulled off the object.
 */
export type StorageManagerLike = {
  readonly persisted: () => Promise<boolean>;
  readonly persist: () => Promise<boolean>;
};

export type PersistenceOutcome =
  /** Now persistent, because we asked. */
  | 'granted'
  /** Already persistent; nothing was asked. */
  | 'already'
  /** The browser said no. The headset works; its storage is evictable. */
  | 'refused'
  /** No such API, or it threw. Nothing to do. */
  | 'unsupported';

/**
 * Makes this origin's storage persistent, if the browser will have it.
 *
 * Never throws and never blocks anything: a headset that cannot get the
 * guarantee still has to boot and run a lesson.
 */
export async function keepPairingOnDevice(
  manager: StorageManagerLike | undefined = globalThis.navigator?.storage,
): Promise<PersistenceOutcome> {
  if (!manager?.persist || !manager.persisted) return 'unsupported';

  try {
    // Asked first: Chromium counts repeated requests against an origin's
    // standing, and persistent is already persistent.
    if (await manager.persisted()) return 'already';
    return (await manager.persist()) ? 'granted' : 'refused';
  } catch {
    // Some browsers reject rather than resolving false.
    return 'unsupported';
  }
}
